import { Box, Container, FormHelperText, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Button from '../foundation/Button';

type FeedbackDialogContentProps = {
    $severity: 'error' | 'info'
};

type MainPanelProps = {
    $centerContent?: boolean
};

type ExpressUploadAreaProps = {
    $lifted?: boolean
};

export const PageShell = styled('div')(({ theme }) => ({
    background: '#ffffff',
    minHeight: '100vh',
    paddingBlockEnd: '3rem',
    [theme.breakpoints.down('sm')]: {
        paddingBlockEnd: '2rem',
    },
}));

export const HeroSection = styled('section')(({ theme }) => ({
    background: '#ffffff',
    direction: 'rtl',
    fontFamily: "'Assistant', 'Open Sans Hebrew', system-ui, -apple-system, 'Segoe UI', sans-serif",
    overflow: 'hidden',
    paddingBlock: '0.25rem 0.75rem',
    [theme.breakpoints.down('md')]: {
        paddingBlock: '0 0.75rem',
    },
    [theme.breakpoints.down('sm')]: {
        paddingBlock: '1.25rem 0.75rem',
    },
}));

export const HeroContainer = styled(Container)(({ theme }) => ({
    alignItems: 'center',
    direction: 'ltr',
    display: 'grid',
    gap: '1.5rem',
    gridTemplateAreas: '"visual content"',
    gridTemplateColumns: '1.05fr 1fr',
    [theme.breakpoints.down('md')]: {
        gap: '0.5rem',
        gridTemplateAreas: '"visual" "content"',
        gridTemplateColumns: '1fr',
    },
    [theme.breakpoints.down('sm')]: {
        gap: '0.75rem',
        gridTemplateAreas: '"visual content"',
        gridTemplateColumns: 'minmax(7rem, 40%) minmax(0, 1fr)',
    },
}));

export const HeroContent = styled(Box)(({ theme }) => ({
    alignSelf: 'center',
    direction: 'rtl',
    display: 'grid',
    gridArea: 'content',
    justifyItems: 'start',
    paddingInlineStart: '0.625rem',
    textAlign: 'right',
    width: '100%',
    [theme.breakpoints.up('md')]: {
        transform: 'translateY(-1rem)',
    },
    [theme.breakpoints.down('md')]: {
        justifyItems: 'center',
        paddingInlineStart: 0,
        textAlign: 'center',
    },
    [theme.breakpoints.down('sm')]: {
        justifyItems: 'end',
        textAlign: 'right',
    },
}));

export const HeroTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.main,
    direction: 'rtl',
    fontFamily: 'inherit',
    fontSize: 'clamp(38px, 4.6vw, 62px)',
    fontWeight: 800,
    lineHeight: 1.08,
    marginBottom: '0.875rem',
    textAlign: 'right',
    width: '100%',
    [theme.breakpoints.down('md')]: {
        fontSize: 'clamp(30px, 8vw, 38px)',
        textAlign: 'center',
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: 'clamp(24px, 7vw, 30px)',
        marginBottom: '0.4rem',
        textAlign: 'right',
    },
}));

export const HeroTitleAccent = styled('span')(({ theme }) => ({
    color: theme.palette.primary.main,
    display: 'block',
}));

export const HeroBrand = styled(Typography)({
    color: '#20283a',
    direction: 'ltr',
    fontFamily: 'inherit',
    fontSize: 'clamp(24px, 2.5vw, 32px)',
    fontWeight: 800,
    lineHeight: 1.2,
    marginBottom: '1.125rem',
    '@media (max-width: 599.95px)': {
        fontSize: '1.15rem',
        marginBottom: '0.75rem',
    },
});

export const HeroSubtitle = styled(Typography)(({ theme }) => ({
    color: '#5B6473',
    direction: 'rtl',
    fontFamily: 'inherit',
    fontSize: 'clamp(16px, 1.25vw, 19px)',
    lineHeight: 1.55,
    marginBottom: '1.75rem',
    maxWidth: '35rem',
    textAlign: 'right',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        display: 'none',
    },
}));

export const HeroCtaButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.secondary.main,
    borderRadius: 12,
    boxShadow: '0 10px 24px rgba(16, 51, 75, 0.26)',
    fontFamily: 'inherit',
    fontSize: '1.25rem',
    fontWeight: 700,
    maxWidth: '37.5rem',
    minHeight: '3.875rem',
    textAlign: 'center',
    width: '100%',
    '&:hover': {
        backgroundColor: '#0b2537',
        transform: 'translateY(-1px)',
    },
    [theme.breakpoints.down('sm')]: {
        borderRadius: 10,
        fontSize: '0.95rem',
        maxWidth: '14rem',
        minHeight: '2.75rem',
    },
}));

export const HeroNotes = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    color: '#a0a7b4',
    direction: 'rtl',
    display: 'flex',
    flexWrap: 'wrap',
    fontFamily: 'inherit',
    fontSize: '0.95rem',
    gap: '0.625rem 0.875rem',
    justifyContent: 'center',
    marginTop: '1rem',
    maxWidth: '37.5rem',
    [theme.breakpoints.down('sm')]: {
        display: 'none',
    },
}));

export const HeroNote = styled('span')({
    whiteSpace: 'nowrap',
});

export const HeroDot = styled('span')({
    background: '#CDD3DB',
    borderRadius: '50%',
    flex: '0 0 auto',
    height: 4,
    width: 4,
});

export const HeroVisual = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gridArea: 'visual',
    justifyContent: 'center',
    minHeight: '27.5rem',
    [theme.breakpoints.down('md')]: {
        minHeight: 0,
    },
    [theme.breakpoints.down('sm')]: {
        justifyContent: 'center',
    },
}));

export const HeroImage = styled('img')(({ theme }) => ({
    display: 'block',
    height: 'auto',
    maxWidth: '35rem',
    width: '100%',
    [theme.breakpoints.down('md')]: {
        maxWidth: '27.5rem',
    },
    [theme.breakpoints.down('sm')]: {
        maxWidth: '9.75rem',
    },
}));

export const FormSection = styled('section')(({ theme }) => ({
    background: '#ffffff',
    paddingBlock: '0 3rem',
    [theme.breakpoints.down('sm')]: {
        paddingBlock: '0 2rem',
    },
}));

export const PageContainer = styled(Container)(({ theme: _theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
}));

export const MainPanel = styled(Box, {
    shouldForwardProp: prop => prop !== '$centerContent',
})<MainPanelProps>(({ theme, $centerContent }) => ({
    background: '#ffffff',
    border: '1px solid #e0e5ec',
    borderRadius: 18,
    boxShadow: '0 18px 50px rgba(17, 37, 61, 0.08)',
    ...($centerContent ? {
        alignContent: 'center',
        display: 'grid',
    } : {}),
    minHeight: '39rem',
    padding: '3rem',
    width: '100%',
    maxWidth: '76rem',
    [theme.breakpoints.down('md')]: {
        minHeight: '34rem',
        padding: '2rem',
    },
    [theme.breakpoints.down('sm')]: {
        borderRadius: 14,
        minHeight: '35rem',
        padding: '1.25rem 1.25rem 2.5rem',
    },
}));

export const HeaderBlock = styled(Box)({
    display: 'none',
});

export const SectionTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.main,
    fontWeight: 700,
    marginBottom: '0.75rem',
}));

export const FormFields = styled(Box)({
    display: 'grid',
    gap: '0.75rem',
});

export const ButtonStack = styled(Box)({
    display: 'grid',
    gap: '0.75rem',
});

export const DividerRow = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    gap: '0.75rem',
    marginBlock: '1rem',
});

export const DividerLine = styled('div')({
    height: 1,
    background: '#e0e5e8',
});

export const SelectedFileText = styled(Typography)({
    wordBreak: 'break-word',
});

export const TermsText = styled('span')({
    lineHeight: 1.7,
});

export const ExpressUploadArea = styled(Box, {
    shouldForwardProp: prop => prop !== '$lifted',
})<ExpressUploadAreaProps>(({ theme, $lifted }) => ({
    display: 'grid',
    gap: '0.875rem',
    marginInline: 'auto',
    maxWidth: '66rem',
    textAlign: 'center',
    transform: $lifted ? 'translateY(-1rem)' : undefined,
    width: '100%',
    '& .MuiAlert-root': {
        marginInline: 'auto',
        maxWidth: '34rem',
    },
    '& .MuiTypography-root': {
        wordBreak: 'break-word',
    },
    [theme.breakpoints.down('sm')]: {
        transform: $lifted ? 'translateY(-0.6rem)' : undefined,
    },
}));

export const ExpressDropZoneFrame = styled(Box)(({ theme }) => ({
    '& > div': {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: '13rem',
    },
    [theme.breakpoints.down('sm')]: {
        '& > div': {
            minHeight: '10rem',
        },
    },
}));

export const ExpressConsentGrid = styled(Box)(({ theme }) => ({
    alignItems: 'start',
    direction: 'rtl',
    display: 'grid',
    gap: '1.5rem',
    marginInline: 'auto',
    marginBlockStart: '3rem',
    maxWidth: '54rem',
    width: '100%',
    [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    },
    '& .MuiFormGroup-root': {
        direction: 'rtl',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        textAlign: 'right',
        width: '100%',
    },
    [theme.breakpoints.down('sm')]: {
        gap: '1.25rem',
        justifyItems: 'stretch',
        marginBlockStart: '2.25rem',
    },
}));

export const ExpressConsentToggleRow = styled('label')(({ theme: _theme }) => ({
    alignItems: 'center',
    cursor: 'pointer',
    direction: 'ltr',
    display: 'inline-flex',
    flexDirection: 'row',
    gap: '0.75rem',
    justifyContent: 'flex-start',
    margin: 0,
    textAlign: 'left',
    width: '100%',
}));

export const ExpressConsentLabel = styled('span')(({ theme }) => ({
    color: theme.palette.secondary.main,
    direction: 'ltr',
    flex: '0 1 auto',
    fontSize: '1.1rem',
    fontWeight: 700,
    lineHeight: 1.35,
    minWidth: 0,
    textAlign: 'left',
    unicodeBidi: 'isolate',
}));

export const ExpressRequiredMark = styled('span')(({ theme }) => ({
    color: theme.palette.secondary.main,
    marginInlineStart: '0.25rem',
}));

export const ExpressConsentDivider = styled('div')(({ theme }) => ({
    alignSelf: 'stretch',
    background: '#dfe3e8',
    display: 'none',
    width: 1,
    [theme.breakpoints.up('md')]: {
        display: 'block',
    },
}));

export const ExpressConsentText = styled(FormHelperText)(({ theme }) => ({
    color: theme.palette.text.secondary,
    direction: 'ltr',
    fontSize: '0.95rem',
    lineHeight: 1.7,
    marginInline: 0,
    width: '100%',
    textAlign: 'left',
    unicodeBidi: 'isolate',
}));

export const ExpressActions = styled(Box)(({ theme }) => ({
    display: 'grid',
    gap: '1rem',
    marginInline: 'auto',
    marginBlockStart: '2rem',
    maxWidth: '40rem',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: '1fr 1fr',
    },
    '& .MuiButtonBase-root': {
        minHeight: '3.5rem',
    },
}));

export const LoggedInUploadAction = styled(Box)({
    marginBlockStart: '2rem',
    marginInline: 'auto',
    maxWidth: '32rem',
    width: '100%',
});

export const ExpressGoogleAction = styled(Box)(({ theme }) => ({
    '& .MuiButtonBase-root': {
        minHeight: '3.5rem',
    },
    '& .MuiButton-startIcon': {
        marginInlineEnd: '0.5rem',
        marginInlineStart: 0,
    },
    '& .MuiButtonBase-root:hover': {
        borderColor: theme.palette.primary.dark,
        color: theme.palette.primary.dark,
    },
}));

export const SuccessContent = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    alignContent: 'center',
    display: 'grid',
    gap: '1.35rem',
    justifyItems: 'center',
    minHeight: '32rem',
    paddingBlock: '2rem',
    textAlign: 'center',
    width: '100%',
    '& .MuiTypography-h3': {
        color: theme.palette.secondary.main,
        fontSize: 'clamp(2rem, 4vw, 3.25rem)',
        fontWeight: 700,
        lineHeight: 1.22,
    },
    '& .MuiTypography-h5': {
        color: theme.palette.text.secondary,
        fontSize: 'clamp(1.15rem, 2vw, 1.55rem)',
        lineHeight: 1.45,
        marginBlockStart: '-1.1rem',
    },
    [theme.breakpoints.down('sm')]: {
        gap: '1rem',
        minHeight: '27rem',
        paddingBlock: '1.25rem',
        '& .MuiTypography-h5': {
            marginBlockStart: '-0.85rem',
        },
    },
}));

export const SuccessIcon = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    background: 'rgba(52, 126, 62, 0.1)',
    borderRadius: '50%',
    color: theme.palette.primary.main,
    display: 'flex',
    height: '6rem',
    justifyContent: 'center',
    marginBlock: '0 0.5rem',
    transform: 'translateY(-2rem)',
    width: '6rem',
    '& .MuiSvgIcon-root': {
        fontSize: '4.5rem',
        strokeWidth: 1.2,
    },
    [theme.breakpoints.down('sm')]: {
        height: '4.5rem',
        marginBlock: '0 0.35rem',
        transform: 'translateY(-1.25rem)',
        width: '4.5rem',
        '& .MuiSvgIcon-root': {
            fontSize: '3.25rem',
        },
    },
}));

export const SuccessActions = styled(Box)(({ theme }) => ({
    display: 'grid',
    gap: '1.25rem',
    marginBlockStart: '1rem',
    maxWidth: '44rem',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: '1fr 1fr',
    },
    '& .MuiButtonBase-root': {
        fontSize: '1.2rem',
        minHeight: '4.25rem',
    },
}));

export const FeedbackDialogIcon = styled('div')<FeedbackDialogContentProps>(({ theme, $severity }) => ({
    alignItems: 'center',
    background: $severity === 'error' ? 'rgba(211, 83, 58, 0.12)' : 'rgba(16, 51, 75, 0.1)',
    borderRadius: '50%',
    color: $severity === 'error' ? theme.palette.error.main : theme.palette.secondary.main,
    display: 'flex',
    height: '5.5rem',
    justifyContent: 'center',
    marginInline: 'auto',
    width: '5.5rem',
}));

export const FeedbackDialogContent = styled('div')<FeedbackDialogContentProps>(({ theme }) => ({
    color: theme.palette.text.primary,
    direction: 'rtl',
    display: 'grid',
    gap: '1.25rem',
    justifyItems: 'center',
    lineHeight: 1.8,
    textAlign: 'center',
    whiteSpace: 'pre-line',
    '& .MuiTypography-body1': {
        fontSize: '1.15rem',
        lineHeight: 1.75,
    },
}));
